<template>
    <div class="profiles-page">
        <div class="page-route-select">
            <router-link to="/dashboard" class="page-route-select-text">
                Gerencial
            </router-link>
            <span class="material-icons page-route-select-icon">play_arrow</span>
            <div class="page-route-select-text">Perfis</div>
        </div>

        <div class="profiles-page__header">
            <div>
                <h1 class="profiles-page__title">Gestao de Perfis</h1>
                <p class="profiles-page__subtitle">
                    Consulte os perfis base e os perfis customizados com filtros por dono e permissao.
                </p>
            </div>
        </div>

        <div class="profiles-page__tabs">
            <button
                v-for="tab in tabs"
                :key="tab.id"
                :class="['profiles-page__tab', { 'profiles-page__tab--active': activeTab === tab.id }]"
                @click="changeTab(tab.id)"
            >
                {{ tab.label }}
            </button>
        </div>

        <div class="profiles-page__filters">
            <label class="profiles-page__field">
                <span>Perfil</span>
                <input v-model.trim="filters.name" type="text" placeholder="Nome do perfil" />
            </label>
            <label class="profiles-page__field">
                <span>Cliente / Fornecedor</span>
                <input v-model.trim="filters.owner_name" type="text" placeholder="Nome do dono do perfil" />
            </label>
            <label class="profiles-page__field">
                <span>Permissao recurso</span>
                <input v-model.trim="filters.permission_resource" type="text" placeholder="RESOURCE" />
            </label>
            <label class="profiles-page__field">
                <span>Permissao acao</span>
                <input v-model.trim="filters.permission_action" type="text" placeholder="READ" />
            </label>
            <button class="profiles-page__ghost-button" :disabled="loading" @click="loadProfiles">
                Filtrar
            </button>
            <button class="profiles-page__ghost-button" :disabled="loading" @click="clearFilters">
                Limpar
            </button>
        </div>

        <div v-if="successMessage" class="profiles-page__feedback profiles-page__feedback--success">
            {{ successMessage }}
        </div>
        <div v-if="error" class="profiles-page__feedback profiles-page__feedback--error">
            {{ error }}
        </div>

        <div class="profiles-page__table-wrapper">
            <div v-if="loading" class="profiles-page__table-loader">
                <div class="cc-loader">
                    <img src="/images/loader.svg" />
                </div>
            </div>
            <table class="profiles-page__table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Perfil</th>
                        <th>Tipo</th>
                        <th>Dono</th>
                        <th>Usuários</th>
                        <th v-if="activeTab === 'base'">Visibilidade</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="profile in profiles" :key="profile.id">
                        <td>#{{ profile.id }}</td>
                        <td>
                            <div class="profiles-page__name-cell">
                                <strong>{{ profile.name }}</strong>
                                <span v-if="profile.legacy_profile_id" class="profiles-page__legacy">
                                    legado #{{ profile.legacy_profile_id }}
                                </span>
                            </div>
                        </td>
                        <td>
                            <span
                                :class="[
                                    'profiles-page__badge',
                                    profile.is_base ? 'profiles-page__badge--base' : 'profiles-page__badge--owned',
                                ]"
                            >
                                {{ profile.is_base ? "Base" : "Customizado" }}
                            </span>
                        </td>
                        <td>
                            <div class="profiles-page__owner-cell">
                                <strong>{{ profile.owner_name || "-" }}</strong>
                                <span>{{ ownerLabel(profile) }}</span>
                            </div>
                        </td>
                        <td>{{ profile.users_count != null ? profile.users_count : 0 }}</td>
                        <td v-if="activeTab === 'base'">
                            <select
                                class="profiles-page__visibility-select"
                                :value="profile.visibility || 'all'"
                                :disabled="savingVisibility === profile.id"
                                @change="updateVisibility(profile, $event.target.value)"
                            >
                                <option value="all">Todos</option>
                                <option value="client">Cliente</option>
                                <option value="provider">Fornecedor</option>
                                <option value="admin">Admin</option>
                            </select>
                        </td>
                        <td>
                            <button class="profiles-page__edit-btn" @click="editProfile(profile)" title="Editar Perfil">
                                <i class="fas fa-user-shield"></i>
                            </button>
                            <button v-if="activeTab === 'base'" class="profiles-page__delete-btn" :disabled="deletingId === profile.id" @click="deleteProfile(profile)" title="Excluir Perfil">
                                <i class="fas fa-trash-alt"></i>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="!loading && profiles.length === 0">
                        <td :colspan="activeTab === 'base' ? 7 : 6" class="profiles-page__empty-state">
                            Nenhum perfil encontrado para os filtros informados.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <cc-pagination
            v-if="pagination.total > pagination.per_page"
            class="profiles-page__pagination"
            :items_by_page_value="pagination.per_page"
            :total="pagination.total"
            :page="pagination.current_page"
            :page_limit="10"
            classes="orange"
            @change="handlePageChange"
        />

        <div v-if="loading" class="overlay">
            <div class="cc-loader">
                <img src="/images/loader.svg" />
            </div>
        </div>
        
        <edit-profile-modal 
            v-if="showEditModal" 
            :profile="editingProfile" 
            :closeDialog="closeEditModal" 
            @profile-updated="onProfileUpdated" 
        />
    </div>
</template>

<script>
import ProfileManagementService from "@/services/v3/management/profile.service";
import Pagination from "@/components/cliente/base-components/Pagination";
import EditProfileModal from "@/modals/permissions/edit-profile.modal.vue";

export default {
    name: "ProfilesPage",
    components: {
        ccPagination: Pagination,
        EditProfileModal,
    },
    data() {
        return {
            profileService: new ProfileManagementService(),
            activeTab: "owned",
            profiles: [],
            pagination: {
                current_page: 1,
                per_page: 15,
                total: 0,
            },
            filters: {
                name: "",
                owner_name: "",
                permission_resource: "",
                permission_action: "",
            },
            tabs: [
                { id: "base", label: "Perfis Base" },
                { id: "owned", label: "Perfis Customizados" },
            ],
            loading: false,
            error: null,
            successMessage: null,
            savingVisibility: null,
            deletingId: null,
            editingProfile: null,
            showEditModal: false,
        };
    },
    methods: {
        editProfile(profile) {
            this.editingProfile = profile;
            this.showEditModal = true;
        },
        closeEditModal() {
            this.showEditModal = false;
            this.editingProfile = null;
        },
        onProfileUpdated() {
            this.loadProfiles();
        },
        async loadProfiles(page = this.pagination.current_page) {
            this.loading = true;
            this.error = null;

            try {
                const response = await this.profileService.list({
                    page,
                    tab: this.activeTab,
                    ...this.filters,
                });

                this.profiles = response.data.data || [];
                const meta = response.data.meta || {};
                this.pagination = {
                    current_page: meta.current_page || 1,
                    per_page: meta.per_page || 15,
                    total: meta.total || this.profiles.length,
                };
            } catch (error) {
                this.error = "Erro ao carregar os perfis.";
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        changeTab(tabId) {
            this.activeTab = tabId;
            this.loadProfiles(1);
        },
        clearFilters() {
            this.filters = {
                name: "",
                owner_name: "",
                permission_resource: "",
                permission_action: "",
            };
            this.loadProfiles(1);
        },
        handlePageChange(page) {
            if (page === this.pagination.current_page || this.loading) {
                return;
            }

            this.loadProfiles(page);
        },
        ownerLabel(profile) {
            if (profile.owner_type === "client") {
                return "Cliente";
            }

            if (profile.owner_type === "provider") {
                return "Fornecedor";
            }

            return "Sem dono";
        },
        extractError(error, fallbackMessage) {
            const apiErrors = error && error.response && error.response.data && error.response.data.errors;

            if (Array.isArray(apiErrors) && apiErrors.length > 0) {
                return apiErrors.join(" ");
            }

            if (apiErrors && typeof apiErrors === "object") {
                return Object.values(apiErrors)
                    .flat()
                    .join(" ");
            }

            return fallbackMessage;
        },
        async updateVisibility(profile, visibility) {
            this.savingVisibility = profile.id;
            this.error = null;
            try {
                const response = await this.profileService.updateVisibility(profile.id, visibility);
                profile.visibility = response.data.data ? response.data.data.visibility : visibility;
                this.successMessage = `Visibilidade de "${profile.name}" atualizada.`;
                setTimeout(() => (this.successMessage = null), 3000);
            } catch (e) {
                this.error = this.extractError(e, "Erro ao atualizar visibilidade.");
            } finally {
                this.savingVisibility = null;
            }
        },
        async deleteProfile(profile) {
            if (!confirm(`Excluir o perfil base "${profile.name}"? Esta ação não pode ser desfeita.`)) return;
            this.deletingId = profile.id;
            this.error = null;
            try {
                await this.profileService.remove(profile.id);
                this.profiles = this.profiles.filter((p) => p.id !== profile.id);
                this.successMessage = `Perfil "${profile.name}" excluído.`;
                setTimeout(() => (this.successMessage = null), 3000);
            } catch (e) {
                this.error = this.extractError(e, "Erro ao excluir perfil.");
            } finally {
                this.deletingId = null;
            }
        },
    },
    mounted() {
        this.loadProfiles();
    },
};
</script>

<style scoped lang="scss">
.profiles-page {
    padding: 0.75rem 2rem 2rem 0;
}

.profiles-page__header {
    margin: 1rem 0 1.25rem;
}

.profiles-page__title {
    margin: 0;
    font-size: 2rem;
    color: #303030;
}

.profiles-page__subtitle {
    margin: 0.35rem 0 0;
    color: #6e6e6e;
}

.profiles-page__tabs {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.profiles-page__tab {
    border: 1px solid #ead7cb;
    background: #fff;
    color: #7c5a44;
    border-radius: 999px;
    padding: 0.75rem 1rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
}

.profiles-page__tab--active {
    background: #ffefe3;
    border-color: #ff7110;
    color: #c85600;
}

.profiles-page__tab-count {
    background: rgba(255, 113, 16, 0.12);
    border-radius: 999px;
    padding: 0.1rem 0.45rem;
    font-size: 0.85rem;
}

.profiles-page__filters {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem;
}

.profiles-page__filters {
    margin-bottom: 1rem;
    align-items: end;
}

.profiles-page__field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    color: #4b4b4b;
    font-size: 0.95rem;
}

.profiles-page__field input,
.profiles-page__field select {
    width: 100%;
    border: 1px solid #d7d7d7;
    border-radius: 8px;
    padding: 0.7rem 0.8rem;
    background: #fff;
    font: inherit;
    color: #303030;
}

.profiles-page__ghost-button {
    border: none;
    border-radius: 10px;
    cursor: pointer;
    padding: 0.85rem 1rem;
    font-weight: 700;
}

.profiles-page__ghost-button {
    background: #fff3ea;
    color: #c95d10;
}

.profiles-page__feedback {
    margin-bottom: 1rem;
    padding: 0.9rem 1rem;
    border-radius: 10px;
    font-weight: 600;
}

.profiles-page__feedback--success {
    background: #edf9f0;
    color: #207245;
}

.profiles-page__feedback--error {
    background: #ffefef;
    color: #b02a37;
}

.profiles-page__table-wrapper {
    position: relative;
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 18px;
    overflow: auto;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.03);
    backdrop-filter: blur(10px);
}

.profiles-page__table-loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.75);
    z-index: 1;
}

.profiles-page__table {
    width: 100%;
    min-width: 1100px;
    border-collapse: collapse;
}

.profiles-page__pagination {
    margin-top: 1rem;
}

.profiles-page__table th,
.profiles-page__table td {
    padding: 1rem;
    border-bottom: 1px solid #f1f1f1;
    vertical-align: top;
    text-align: left;
}

.profiles-page__table th {
    color: #686868;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: #fffaf6;
    border-bottom: 2px solid #f9ece2;
}

.profiles-page__table tr {
    transition: all 0.2s ease;

    &:hover:not(:has(th)) {
        background-color: #fdfbff;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
    }
}

.profiles-page__name-cell,
.profiles-page__owner-cell {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.profiles-page__legacy {
    color: #8b8b8b;
    font-size: 0.85rem;
}

.profiles-page__badge {
    display: inline-flex;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 700;
}

.profiles-page__badge--base {
    background: #edf5ff;
    color: #1b5fb8;
}

.profiles-page__badge--owned {
    background: #fff1e7;
    color: #b95a14;
}

.profiles-page__permission-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.profiles-page__permission-chip {
    display: inline-flex;
    padding: 0.35rem 0.6rem;
    border-radius: 999px;
    background: #f7f7f7;
    color: #505050;
    font-size: 0.84rem;
}

.profiles-page__empty-state {
    text-align: center;
    color: #6e6e6e;
}

.profiles-page__visibility-select {
    border: 1px solid #d7d7d7;
    border-radius: 8px;
    padding: 0.45rem 0.6rem;
    background: #fff;
    font: inherit;
    color: #303030;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.profiles-page__edit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #f0f4ff;
    border: 1px solid #c6d4f5;
    border-radius: 8px;
    color: #2a5cb0;
    padding: 0.4rem;
    cursor: pointer;
    margin-right: 0.5rem;

    i {
        font-size: 1.05rem;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: #e0e8ff;
        transform: scale(1.05);
        color: #1b4a9c;
    }
}

.profiles-page__delete-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff0f0;
    border: 1px solid #f5c6c6;
    border-radius: 8px;
    color: #b02a37;
    padding: 0.4rem;
    cursor: pointer;

    i {
        font-size: 1.05rem;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: #ffe0e0;
        transform: scale(1.05);
        color: #8b1d28;
    }
}

.page-route-select {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #7b7b7b;
    font-size: 0.95rem;
}

.page-route-select-text {
    color: inherit;
    text-decoration: none;
}

.page-route-select-icon {
    font-size: 1rem;
}

@media only screen and (max-width: 1080px) {
    .profiles-page {
        padding: 0.75rem 1rem 1.5rem 0;
    }

    .profiles-page__tabs {
        flex-wrap: wrap;
    }

    .profiles-page__filters,
    .profiles-page__batch-form {
        grid-template-columns: 1fr;
    }
}
</style>
